import React, { useEffect, useState } from "react"
import Spinner from "./spinner"
import Pagination from "./pagination";
import { User } from "@ecountabl/lib";
import api, { UserAdminView } from './api-client'
// styles
import './user-manager.scss'
import { useDispatch, useSelector } from "react-redux";
import notify from './notifications-helper'

const sortOptions = [
  { value: "oldest", label: "Oldest" },
  { value: "newest", label: "Newest" },
  { value: "email a-z", label: "Email A-Z" },
  { value: "email z-a", label: "Email Z-A" }
]

const userTypeOptions = [
  { value: "all", label: "All" },
  { value: "super-user", label: "Super User" },
  { value: "user", label: "User" }
]

type Query = {
  page: number;
  // sort can be any value in sortOptions
  sort: "oldest" | "newest" | "email a-z" | "email z-a";
  type: "all" | "super-user" | "user";
  search: string;
  limit: number;
  approval: "all" | "approved" | "unapproved";
}

const UserManager = () => {
  const [users, setUsers] = useState<UserAdminView[]>([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState<Query>({
    page: 1,
    sort: "newest",
    search: "",
    type: "all",
    limit: 10,
    approval: "all"
  })

  const dispatch = useDispatch()

  const onChangeQuery = (key: keyof Query, value: any) => {
    setQuery({
      ...query,
      [key]: value
    })
  }

  const changeUserLevel = async (index) => {
    const user = users[index]
    // prompt the user via window to confirm
    if (!window.confirm(
      user.isSuperUser ? 
      `Are you sure you want to revoke SuperUser status from ${user.email}?`
      : `Are you sure you want to make ${user.email} a SuperUser?`
    )) {
      return
    }
    
    const newLevel = user.isSuperUser ? false : true
    setLoading(true)
    try {
      const newUser = await api.updateUser({...user, isSuperUser: newLevel})

      if (!newUser || !newUser.user) {
        throw new Error("Failed to update user")
      }

      setUsers([
        ...users.slice(0, index),
        newUser.user,
        ...users.slice(index + 1)
      ])

      dispatch(notify.success({message: `User ${newUser.user.email} has been ${newLevel ? "promoted to superuser" : "demoted to user"}`}))
    } catch (error) {
      dispatch(notify.error({message: error.message || "Failed to update user"}))
      return
    } finally {
      setLoading(false)
    }
  }

  const deleteUser = async (index: number) => {
    const user = users[index]
    // first confirm
    const first = window.confirm(`Delete ${user.email}? This will permanently remove the user and related data.`)
    if (!first) return
    // second confirm
    const second = window.confirm(`Are you absolutely sure? This action cannot be undone.`)
    if (!second) return

    setLoading(true)
    try {
      await api.purgeUser(String(user._id))
      setUsers([
        ...users.slice(0, index),
        ...users.slice(index + 1)
      ])
      dispatch(notify.success({ message: `User ${user.email} has been deleted` }))
    } catch (error) {
      dispatch(notify.error({ message: error.message || "Failed to delete user" }))
      return
    } finally {
      setLoading(false)
    }
  }

  const toggleUserOnboarded = async (index) => {
    const user = users[index]
    const nextHasOnboarded = !user.hasOnboarded
    if (!window.confirm(`Are you sure you want to mark ${user.email} as ${nextHasOnboarded ? 'onboarded' : 'not onboarded'}?`)) {
      return
    }
    setLoading(true)
    try {
      const newUser = await api.updateUser({ _id: user._id, hasOnboarded: nextHasOnboarded })
      if (!newUser || !newUser.user) {
        throw new Error("Failed to update user onboarded status")
      }
      setUsers([
        ...users.slice(0, index),
        newUser.user,
        ...users.slice(index + 1)
      ])
      dispatch(notify.success({ message: `User ${newUser.user.email} onboarding set to ${newUser.user.hasOnboarded}` }))
    } catch (error) {
      dispatch(notify.error({ message: error.message || "Failed to update user onboarded status" }))
      return
    } finally {
      setLoading(false)
    }
  }

  const toggleUserApproval = async (index) => {
    const user = users[index]
    const nextApproved = !user.approved
    const actionText = nextApproved ? 'approve' : 'revoke approval for'
    if (!window.confirm(`Are you sure you want to ${actionText} ${user.email}?`)) {
      return
    }
    setLoading(true)
    try {
      const newUser = await api.updateUser({ ...user, approved: nextApproved })
      if (!newUser || !newUser.user) {
        throw new Error("Failed to update user approval")
      }
      setUsers([
        ...users.slice(0, index),
        newUser.user,
        ...users.slice(index + 1)
      ])
      dispatch(notify.success({ message: `User ${newUser.user.email} has been ${nextApproved ? 'approved' : 'set to unapproved'}` }))
    } catch (error) {
      dispatch(notify.error({ message: error.message || "Failed to update user approval" }))
      return
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    fetchUsers()
  }, [query.page, query.sort, query.type, query.approval])

  const fetchUsers = async () => {
    if (query.search.length > 0 && query.search.length < 3) {
      dispatch(notify.error({message: "Search must be at least 3 characters"}))
      return
    }

    setLoading(true)

    // fetch users from API
    try {
      const {users} = await api.getUsers(query)
      setUsers(users)
    } catch (error) {
      dispatch(notify.error({message: error.message || "Failed to fetch users"}))
      setLoading(false)
      return
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="users-mgr">
      <div style={{ width: '100%' }}>
        <div>
          <h3>User Moderation</h3>
        </div>
        {loading ? <Spinner /> : null}
        <div className='user-mgr-body'>
          <div className='user-control-row'>
            <div className='user-control'>
              <label>Page</label>
              <Pagination
                page={query.page}
                onChange={page => {
                  onChangeQuery("page", page)
                }}
              />
            </div>
            <div className='user-control max-width'>
              <label>Limit</label>
              <select className='form-control' value={query.limit} onChange={(e) => onChangeQuery("limit", e.target.value)}>
                {[10, 25, 50, 100].map(limit => (
                  <option key={limit} value={limit}>{limit}</option>
                ))}
              </select>
            </div>
            <div className='user-control max-width'>
              <label>Sort</label>
              <select className='form-control' value={query.sort} onChange={(e) => onChangeQuery("sort", e.target.value)}>
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className='user-control max-width'>
              <label>Type</label>
              <select className='form-control' value={query.type} onChange={(e) => onChangeQuery("type", e.target.value)}>
                {userTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className='user-control max-width'>
              <label>Approval</label>
              <select
                className='form-control'
                value={query.approval}
                onChange={(e) => onChangeQuery("approval", e.target.value)}
              >
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="unapproved">Unapproved</option>
              </select>
            </div>
            
            <div className='user-control max-width'>
              <label>Search</label>
              <input
                className='form-control'
                type="text"
                value={query.search}
                onChange={(e) => onChangeQuery("search", e.target.value)}
                placeholder='Search...'
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={fetchUsers}
              disabled={loading}
            >
              <i className="fa fa-search" />
            </button>
          </div>
          { users.length === 0 && <h5>No Users found</h5>}
          { users.length > 0 && users.map((user, idx) => (
              <UserItem
                key={idx}
                user={user}
                index={idx}
                changeUserLevel={changeUserLevel}
                toggleUserApproval={toggleUserApproval}
                toggleUserOnboarded={toggleUserOnboarded}
                deleteUser={deleteUser}
              />
            ))
          }
        </div>
      </div>
    </div>
  )
}

type UserItemProps = {
  user: UserAdminView;
  index: number;
  changeUserLevel: (index: number) => void;
  toggleUserApproval: (index: number) => void;
  toggleUserOnboarded: (index: number) => void;
  deleteUser: (index: number) => void;
}

const UserItem = ({user, index, changeUserLevel, toggleUserApproval, toggleUserOnboarded, deleteUser}: UserItemProps) => {
  const curUser = useSelector((state: any) => state.user)
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState(false)
  const [expandedAccounts, setExpandedAccounts] = useState(false)
  const [expandedImpactAreas, setExpandedImpactAreas] = useState(false)
  const [expandedDevices, setExpandedDevices] = useState(false)
  const numAccounts = user.accounts.length
  const hasValues = Object.values(user.valuedImpactAreaMap).some(val => val)
  const deviceEntries = Object.entries((user as any).appPermissions || {}) as [string, any][]
  const numDevices = deviceEntries.length


  const isSelf = curUser.user._id === user._id

  return (
    <div className="user-item">
      <div className="main-row interactive" onClick={() => setExpanded(!expanded)} >
        <div className="item">{user.email}</div>
        <div className="item-sm">{user.firstName || '-'}</div>
        <div className="item-sm">{user.lastName || '-'}</div>
        <div className="item-sm">
          <a href={user.customer ? `/customers/${user.customer._id}` : null}>
            {user.customer ? user.customer.name : "No Customer"}
          </a>
        </div>
        
        <div className="item-sm">{user.approved ? "Approved" : "Unapproved"}</div>
        <div className="item-sm">{user.isSuperUser ? "Super User" : "User"}</div>
        <i className={`fa fa-chevron-${expanded ? "up" : "down"}`}/>
      </div>
      {
        expanded && (
          <div className="expanded-section">
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">_id:</label>
                <div className="value">{user._id}</div>
              </div>
              <div className="detail">
                <label className="label">Generation Time:</label>
                <div className="value">{new Date(user.generationTime).toLocaleString()}</div>
              </div>
              <div className="detail">
                <label className="label">Super User:</label>
                {/* display falsey value */}
                <div className="value">{(user.isSuperUser || false).toString()}</div>
              </div>
            </div>
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">Auth0 ID:</label>
                <div className="value">{user.auth0Id || 'undefined'}</div>
              </div>
              <div className="detail">
                <label className="label">Email:</label>
                <div className="value">{user.email}</div>
              </div>
            </div>
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">First Name:</label>
                <div className="value">{user.firstName || '-'}</div>
              </div>
              <div className="detail">
                <label className="label">Last Name:</label>
                <div className="value">{user.lastName || '-'}</div>
              </div>
            </div>
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">Onboarded:</label>
                <div className="value">{(user.hasOnboarded || false).toString()}</div>
              </div>
              <div className="detail">
                <label className="label">Accepted Terms:</label>
                <div className="value">{(user.hasAcceptedTerms || false).toString()}</div>
              </div>
              <div className="detail">
                <label className="label">Needs Value Selection:</label>
                <div className="value">{(user.needsValueSelection || false).toString()}</div>
              </div>
            </div>
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">Customer:</label>
                <div className="value">
                  <a href={user.customer ? `/customers/${user.customer._id}` : null}>
                    {user.customer ? user.customer.name : "No Customer"}
                  </a>
                </div>
              </div>
              <div className="detail">
                <label className="label">Tenant:</label>
                <div className="value">{user.tenant || "undefined"}</div>
              </div>
              <div className="detail">
                <label className="label">Source:</label>
                <div className="value">{user.source || "undefined"}</div>
              </div>
            </div>
            {/* accounts of user */}
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">Accounts:</label>
                <div className="value">
                  {numAccounts} account{numAccounts == 1 ? " " : "s "}
                  {
                    numAccounts > 0 && (
                      <i className={`fa fa-${expandedAccounts ? "minus" : "plus"} interactive`} onClick={() => setExpandedAccounts(!expandedAccounts)}/>
                    )
                  }
                </div>
              </div>
            </div>
            {
              expandedAccounts && (
                <div className="user-detail-row">
                  {user.accounts.map((account: any, idx) => (
                    <div key={idx} className="account">
                      <div>Institution: {account.institution.name}</div>
                      <div>Status: {account.status}</div>
                    </div>
                  ))}
                </div>
              )
            }
            {/* valued impact area map */}
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">Values:</label>
                <div className="value">
                  {
                    hasValues ? (
                      <>
                        See {expandedImpactAreas ? "less" : "more"}{" "}
                        <i className={`fa fa-${expandedImpactAreas ? "minus" : "plus"} interactive`} onClick={() => setExpandedImpactAreas(!expandedImpactAreas)}/>
                      </>
                    ) : (
                      "No values defined"
                    )
                  }
                </div>
              </div>
            </div>
            {
              expandedImpactAreas && (
                <div className="user-detail-row">
                  {Object.keys(user.valuedImpactAreaMap).map((key, idx) => (
                    <div key={idx} className="account">
                      <div>{key}: <i className={`fa fa-${user.valuedImpactAreaMap[key] ? 'check' : 'close'}`}/></div>
                    </div>
                  ))}
                </div>
              )
            }
            {/* devices & app permissions */}
            <div className="user-detail-row">
              <div className="detail">
                <label className="label">Devices:</label>
                <div className="value">
                  {numDevices} device{numDevices === 1 ? " " : "s "}
                  {
                    numDevices > 0 && (
                      <i
                        className={`fa fa-${expandedDevices ? "minus" : "plus"} interactive`}
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedDevices(!expandedDevices)
                        }}
                      />
                    )
                  }
                </div>
              </div>
            </div>
            {
              expandedDevices && numDevices > 0 && (
                <div className="user-detail-row">
                  {deviceEntries.map(([deviceId, perms]) => {
                    const locStatus = perms?.location?.status ?? 'undetermined'
                    const locUpdated = perms?.location?.lastUpdated
                      ? new Date(perms.location.lastUpdated).toLocaleString()
                      : undefined
                    const notifStatus = perms?.notifications?.status ?? 'undetermined'
                    const notifUpdated = perms?.notifications?.lastUpdated
                      ? new Date(perms.notifications.lastUpdated).toLocaleString()
                      : undefined
                    const pushToken = perms?.notifications?.pushToken as string | undefined
                    return (
                      <div key={deviceId} className="account">
                        <div><strong>Device ID:</strong> {deviceId}</div>
                        <div>
                          <strong>Location:</strong> {locStatus}
                          {locUpdated ? ` (last updated ${locUpdated})` : ''}
                        </div>
                        <div>
                          <strong>Notifications:</strong> {notifStatus}
                          {notifUpdated ? ` (last updated ${notifUpdated})` : ''}
                        </div>
                        {pushToken && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                            <strong>Permission token:</strong>
                            <code style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {pushToken}
                            </code>
                            <button
                              type="button"
                              className="btn btn-light"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigator.clipboard.writeText(pushToken)
                                  .then(() => dispatch(notify.success({ message: 'Token copied to clipboard' })))
                                  .catch(() => dispatch(notify.error({ message: 'Failed to copy token' })))
                              }}
                              title="Copy token"
                            >
                              <i className="fa fa-clipboard" />
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            }
            {/* user action items */}
            <div className="user-actions-row">
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => {
                  e.stopPropagation()
                  if (!isSelf) changeUserLevel(index)
                }}
                disabled={isSelf}
                title={isSelf ? "An admin cannot revoke their own Super User status." : undefined}
                style={isSelf ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
              >
                {user.isSuperUser ? "Revoke SuperUser" : "Make SuperUser"}
              </button>
            </div>
            <div className="user-actions-row" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  // Admins CAN toggle approval on their own account
                  toggleUserApproval(index)
                }}
                style={{ marginRight: 8 }}
              >
                {user.approved ? "Revoke Approval" : "Approve"}
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={(e) => {
                  e.stopPropagation()
                  // Admins CAN toggle onboarding on their own account
                  toggleUserOnboarded(index)
                }}
              >
                {user.hasOnboarded ? "Mark as Not Onboarded" : "Mark as Onboarded"}
              </button>
            </div>
            {/* delete icon bottom-left */}
            <div
              className="delete-user-button"
              title={isSelf ? "You cannot delete your own account." : "Delete user"}
              onClick={(e) => {
                e.stopPropagation()
                if (!isSelf) deleteUser(index)
              }}
              style={isSelf ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              <i className="fa fa-trash" aria-hidden="true" />
            </div>
          </div>
        )
      }
    </div>
  )
}

export default UserManager